import React, { useState, useEffect, useCallback } from 'react';
import nbrApi from '../../../services/nbrApi';
import moment from 'moment';
import Chart from 'react-apexcharts';
import FullPageLoader from '../../components/common/FullPageLoader';
import { toast } from 'react-toastify';
import '../../../css/multi-property-monitor.css';

const RANGE_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' }
];

const LAST_REFRESH_STORAGE_KEY = 'pmrLastRefreshMeta';

const getDefaultDateRange = () => {
    const todayStart = moment().startOf('day');
    return {
        from: todayStart.format('YYYY-MM-DD'),
        to: todayStart.format('YYYY-MM-DD')
    };
};

// Colors from logo
const AQUA_COLOR = '#48BAC8'; // Primary color (aqua)
const PURPLE_COLOR = '#9C27B0'; // Purple color
const MEDIUM_GRAY = '#808080'; // Medium gray
const SECONDS_IN_MINUTE = 60;

function PerformanceManagementReport() {
    const selectedLoginData = JSON.parse(localStorage.getItem('selectedLoginData')) || {};
    const propertiesId = selectedLoginData?.properties_id;
    const defaultRange = getDefaultDateRange();

    // State management
    const [fromDate, setFromDate] = useState(defaultRange.from);
    const [toDate, setToDate] = useState(defaultRange.to);
    const [activeRange, setActiveRange] = useState('today');
    const [resources, setResources] = useState([]);
    const [selectedResource, setSelectedResource] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);
    const [lastRefreshFilters, setLastRefreshFilters] = useState(null);
    const [pendingStoredFilters, setPendingStoredFilters] = useState(null);
    const [yAxisMax, setYAxisMax] = useState(null); // null means auto-scale
    const [yAxisMin, setYAxisMin] = useState(null); // null means auto-scale
    const [chartData, setChartData] = useState({
        series: [],
        options: {
            chart: {
                type: 'line',
                height: 450,
                zoom: {
                    enabled: false
                },
                toolbar: {
                    show: false
                }
            },
            title: {
                text: 'Performance Management Report',
                align: 'center',
                style: {
                    fontSize: '20px',
                    color: '#333',
                    fontWeight: 'bold'
                }
            },
            xaxis: {
                type: 'datetime',
                title: {
                    text: 'Date/Time',
                    style: {
                        color: '#333'
                    }
                },
                labels: {
                    style: {
                        colors: '#333',
                        fontSize: '10px'
                    },
                    datetimeUTC: false
                }
            },
            yaxis: {
                title: {
                    text: 'Minutes',
                    style: {
                        color: '#333'
                    }
                },
                labels: {
                    style: {
                        colors: '#333'
                    },
                    formatter: function (val) {
                        return val.toFixed(2);
                    }
                },
                min: undefined, // Will be set dynamically
                max: undefined, // Will be set dynamically
            },
            stroke: {
                curve: 'smooth',
                width: 2
            },
            tooltip: {
                enabled: true,
                theme: 'dark',
                shared: true,
                followCursor: false,
                intersect: false,
                fixed: {
                    enabled: false
                },
                x: {
                    format: 'MMM dd, yyyy HH:mm'
                },
                y: {
                    formatter: function (val) {
                        return val.toFixed(2) + ' minutes';
                    }
                }
            },
            legend: {
                position: 'top',
                horizontalAlign: 'right',
                labels: {
                    colors: '#333'
                }
            },
            grid: {
                borderColor: '#e7e7e7',
                xaxis: {
                    lines: {
                        show: true
                    }
                },
                yaxis: {
                    lines: {
                        show: true
                    }
                },
                padding: {
                    top: 0,
                    right: 20, // Add right padding to prevent scrollbar from cutting off data
                    bottom: 0,
                    left: 0
                }
            },
            // Add a zero line marker
            annotations: {
                yaxis: [
                    {
                        y: 0,
                        borderColor: '#999',
                        borderWidth: 3,
                        borderDashArray: [5, 5],
                        strokeDashArray: 5,
                        opacity: 0.8,
                        label: {
                            style: {
                                color: '#666',
                                background: '#fff',
                                fontSize: '11px',
                                fontWeight: 600,
                                padding: {
                                    left: 6,
                                    right: 6,
                                    top: 3,
                                    bottom: 3
                                },
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            },
                            position: 'right',
                            offsetX: 10
                        }
                    }
                ]
            },
            dataLabels: {
                enabled: false
            }
        }
    });

    // Apply date range presets
    const applyDateRange = (rangeKey) => {
        switch (rangeKey) {
            case 'today': {
                const todayStart = moment().startOf('day');
                setFromDate(todayStart.format('YYYY-MM-DD'));
                setToDate(todayStart.format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            case 'this_week': {
                const startOfWeek = moment().startOf('isoWeek');
                setFromDate(startOfWeek.format('YYYY-MM-DD'));
                setToDate(startOfWeek.clone().endOf('isoWeek').format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            case 'this_month': {
                const startOfMonth = moment().startOf('month');
                setFromDate(startOfMonth.format('YYYY-MM-DD'));
                setToDate(startOfMonth.clone().endOf('month').format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            default:
                break;
        }
    };

    // Fetch resources list
    const fetchResources = async () => {
        if (!propertiesId) {
            toast.error('Property ID not found. Please log in again.');
            return;
        }

        try {
            const response = await nbrApi.post('/gethkresources', {
                properties_id: propertiesId
            });

            if (response?.data && Array.isArray(response.data)) {
                setResources(response.data);
                // Auto-select first resource if available
                if (response.data.length > 0 && !selectedResource) {
                    setSelectedResource(response.data[0].resources_id || response.data[0].id);
                }
            } else {
                setResources([]);
            }
        } catch (error) {
            console.error('Error fetching resources:', error);
            toast.error('Failed to fetch resources. Please try again.');
            setResources([]);
        }
    };

    const storeRefreshMetadata = useCallback((timestamp) => {
        const resourceDetails = resources.find((resource) => (resource.resources_id || resource.id) === selectedResource);
        const meta = {
            timestamp,
            filters: {
                fromDate,
                toDate,
                resourceId: selectedResource,
                resourceName: resourceDetails?.resource_name || resourceDetails?.name || null,
                rangePreset: activeRange
            }
        };
        setLastRefreshTime(timestamp);
        setLastRefreshFilters(meta.filters);
        try {
            localStorage.setItem(LAST_REFRESH_STORAGE_KEY, JSON.stringify(meta));
        } catch (error) {
            console.error('Unable to store refresh metadata', error);
        }
    }, [resources, selectedResource, fromDate, toDate, activeRange]);

    // Fetch cumulative time segments data
    const fetchReportData = useCallback(async () => {
        if (!propertiesId) {
            toast.error('Property ID not found. Please log in again.');
            return;
        }

        if (!selectedResource) {
            toast.error('Please select a resource.');
            return;
        }

        setLoading(true);
        try {
            const response = await nbrApi.post('/getcumulativetimesegments', {
                properties_id: propertiesId,
                resources_id: selectedResource,
                time_start: fromDate,
                time_stop: toDate
            });

            if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
                // Process data for chart
                const processedData = response.data.map(item => ({
                    timestamp: moment(item.time_stop).valueOf(),
                    allocated: (item.cuml_function_seconds_allocated || 0) / SECONDS_IN_MINUTE, // Convert seconds to minutes
                    worked: (item.cuml_elapsed_seconds || 0) / SECONDS_IN_MINUTE, // Convert seconds to minutes
                    variance: (item.cuml_net_seconds || 0) / SECONDS_IN_MINUTE // Convert seconds to minutes
                }));

                // Sort by timestamp
                processedData.sort((a, b) => a.timestamp - b.timestamp);

                // Prepare series data
                const allocatedData = processedData.map(item => ({
                    x: item.timestamp,
                    y: item.allocated
                }));

                const workedData = processedData.map(item => ({
                    x: item.timestamp,
                    y: item.worked
                }));

                const varianceData = processedData.map(item => ({
                    x: item.timestamp,
                    y: item.variance
                }));

                // Calculate Y-axis range from data if not manually set
                let calculatedMin = null;
                let calculatedMax = null;
                
                const allValues = [
                    ...allocatedData.map(p => p.y),
                    ...workedData.map(p => p.y),
                    ...varianceData.map(p => p.y)
                ].filter(v => v !== null && v !== undefined);
                
                if (allValues.length > 0) {
                    calculatedMin = Math.min(...allValues);
                    calculatedMax = Math.max(...allValues);
                    // Add some padding (10% on each side)
                    const padding = Math.max(Math.abs(calculatedMax - calculatedMin) * 0.1, 5);
                    calculatedMin -= padding;
                    calculatedMax += padding;
                    // Ensure min is at least 0 for this report (since we're dealing with time)
                    if (calculatedMin < 0 && Math.min(...varianceData.map(p => p.y)) >= 0) {
                        calculatedMin = 0;
                    }
                }

                // Update chart data
                setChartData(prev => ({
                    ...prev,
                    series: [
                        {
                            name: 'Allocated Time',
                            data: allocatedData,
                            color: MEDIUM_GRAY
                        },
                        {
                            name: 'Worked Time',
                            data: workedData,
                            color: AQUA_COLOR
                        },
                        {
                            name: 'Variance',
                            data: varianceData,
                            color: PURPLE_COLOR
                        }
                    ],
                    options: {
                        ...prev.options,
                        stroke: {
                            curve: 'smooth',
                            width: [2, 2, 2], // Width for each series
                            dashArray: [5, 0, 0] // Dashed for allocated (first), solid for others
                        },
                        colors: [MEDIUM_GRAY, AQUA_COLOR, PURPLE_COLOR],
                        yaxis: {
                            ...prev.options.yaxis,
                            min: yAxisMin !== null ? yAxisMin : calculatedMin,
                            max: yAxisMax !== null ? yAxisMax : calculatedMax
                        }
                    }
                }));

                toast.success(`Report loaded successfully with ${processedData.length} data points`);
                storeRefreshMetadata(moment().toISOString());
            } else {
                setChartData(prev => ({
                    ...prev,
                    series: []
                }));
                toast.info('No data found for the selected date range and resource');
                storeRefreshMetadata(moment().toISOString());
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            toast.error('Failed to fetch report data. Please try again.');
            setChartData(prev => ({
                ...prev,
                series: []
            }));
            storeRefreshMetadata(moment().toISOString());
        } finally {
            setLoading(false);
        }
    }, [propertiesId, selectedResource, fromDate, toDate, storeRefreshMetadata, yAxisMin, yAxisMax]);

    useEffect(() => {
        try {
            const storedMeta = localStorage.getItem(LAST_REFRESH_STORAGE_KEY);
            if (storedMeta) {
                const parsed = JSON.parse(storedMeta);
                if (parsed?.timestamp) {
                    setLastRefreshTime(parsed.timestamp);
                }
                if (parsed?.filters) {
                    setLastRefreshFilters(parsed.filters);
                    if (parsed.filters.fromDate) {
                        setFromDate(parsed.filters.fromDate);
                    }
                    if (parsed.filters.toDate) {
                        setToDate(parsed.filters.toDate);
                    }
                    if (parsed.filters.rangePreset) {
                        setActiveRange(parsed.filters.rangePreset);
                    }
                    setPendingStoredFilters(parsed.filters);
                }
            }
        } catch (error) {
            console.error('Unable to read stored refresh metadata', error);
        }
    }, []);

    useEffect(() => {
        if (pendingStoredFilters && resources.length > 0) {
            const resourceExists = resources.some(
                (resource) => (resource.resources_id || resource.id) === pendingStoredFilters.resourceId
            );

            if (resourceExists && pendingStoredFilters.resourceId) {
                setSelectedResource(pendingStoredFilters.resourceId);
            }

            setPendingStoredFilters(null);
        }
    }, [pendingStoredFilters, resources]);

    // Initialize resources
    useEffect(() => {
        fetchResources();
    }, [propertiesId]);

    // Update chart Y-axis when limits change
    useEffect(() => {
        if (chartData.series.length > 0) {
            setChartData(prev => ({
                ...prev,
                options: {
                    ...prev.options,
                    yaxis: {
                        ...prev.options.yaxis,
                        min: yAxisMin,
                        max: yAxisMax
                    }
                }
            }));
        }
    }, [yAxisMin, yAxisMax]);

    // Auto-fetch data when resource or dates change
    useEffect(() => {
        if (selectedResource && propertiesId) {
            fetchReportData();
        }
    }, [selectedResource, fromDate, toDate, propertiesId, fetchReportData]);

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <h3 className="mb-0 text-primary fw-bold">
                                <i className="bi bi-graph-up me-2"></i>
                                Performance Management Report
                            </h3>
                            <p className="text-muted mb-0 mt-2">
                                View time allocation vs. worked time and variance for housekeeping resources
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className="row mb-2">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row align-items-end g-3">
                                {/* Resource Dropdown */}
                                <div className="col-12 col-md-6 col-lg-3">
                                    <label htmlFor="resourceSelect" className="form-label fw-bold text-primary">
                                        <i className="bi bi-person me-2"></i>
                                        Resource
                                    </label>
                                    <select
                                        id="resourceSelect"
                                        className="form-select"
                                        value={selectedResource || ''}
                                        onChange={(e) => setSelectedResource(e.target.value ? parseInt(e.target.value) : null)}
                                    >
                                        <option value="">Select a resource</option>
                                        {resources.map((resource) => (
                                            <option
                                                key={resource.resources_id || resource.id}
                                                value={resource.resources_id || resource.id}
                                            >
                                                {resource.resource_name || resource.name || `Resource ${resource.resources_id || resource.id}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* From Date */}
                                <div className="col-12 col-md-6 col-lg-2">
                                    <label htmlFor="fromDate" className="form-label fw-bold text-primary">
                                        <i className="bi bi-calendar-event me-2"></i>
                                        From Date
                                    </label>
                                    <input
                                        type="date"
                                        id="fromDate"
                                        className="form-control"
                                        value={fromDate}
                                        onChange={(e) => {
                                            setFromDate(e.target.value);
                                            if (activeRange !== 'custom') {
                                                setActiveRange('custom');
                                            }
                                        }}
                                    />
                                </div>

                                {/* To Date */}
                                <div className="col-12 col-md-6 col-lg-2">
                                    <label htmlFor="toDate" className="form-label fw-bold text-primary">
                                        <i className="bi bi-calendar-check me-2"></i>
                                        To Date
                                    </label>
                                    <input
                                        type="date"
                                        id="toDate"
                                        className="form-control"
                                        value={toDate}
                                        onChange={(e) => {
                                            setToDate(e.target.value);
                                            if (activeRange !== 'custom') {
                                                setActiveRange('custom');
                                            }
                                        }}
                                    />
                                </div>

                                {/* Quick Ranges */}
                                <div className="col-12 col-md-6 col-lg-3">
                                    <label className="form-label fw-bold text-primary d-block">
                                        <i className="bi bi-lightning-charge me-2"></i>
                                        Quick Ranges
                                    </label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {RANGE_OPTIONS.map(option => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`btn btn-sm ${activeRange === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => applyDateRange(option.value)}
                                                style={{ flex: '1 1 auto', minWidth: '100px' }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Generate Button */}
                                <div className="col-12 col-md-12 col-lg-2">
                                    <label className="form-label fw-bold text-primary d-block d-lg-none">
                                        &nbsp;
                                    </label>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary w-100"
                                        onClick={fetchReportData}
                                        disabled={loading || !selectedResource}
                                    >
                                        <i className="bi bi-search me-2"></i>
                                        {loading ? 'Loading...' : 'Generate'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Chart */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0" style={{ position: 'relative' }}>
                        <div className="card-body" style={{ position: 'relative', minHeight: '500px' }}>
                            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                                <div className="text-muted small">
                                    Last refreshed:{' '}
                                    {lastRefreshTime
                                        ? moment(lastRefreshTime).format('MMM DD, YYYY HH:mm:ss')
                                        : 'Not yet refreshed'}
                                    {lastRefreshFilters && (
                                        <>
                                            <span className="mx-2">|</span>
                                            <span>
                                                Filters:&nbsp;
                                                {lastRefreshFilters.resourceName
                                                    ? lastRefreshFilters.resourceName
                                                    : `Resource ${lastRefreshFilters.resourceId}`}
                                                {' • '}
                                                {moment(lastRefreshFilters.fromDate).format('MMM DD, YYYY')} → {moment(lastRefreshFilters.toDate).format('MMM DD, YYYY')}
                                            </span>
                                        </>
                                    )}
                                </div>
                                {chartData.series.length > 0 && (() => {
                                    // Calculate current range from data
                                    let allValues = [];
                                    chartData.series.forEach(s => {
                                        s.data.forEach(point => {
                                            if (point.y !== null && point.y !== undefined) {
                                                allValues.push(point.y);
                                            }
                                        });
                                    });
                                    
                                    let dataMin = allValues.length > 0 ? Math.min(...allValues) : 0;
                                    let dataMax = allValues.length > 0 ? Math.max(...allValues) : 100;
                                    const padding = Math.max(Math.abs(dataMax - dataMin) * 0.1, 5);
                                    dataMin = Math.max(0, dataMin - padding); // Don't go below 0
                                    dataMax += padding;
                                    
                                    // Use current manual limits or calculated range
                                    const currentMax = yAxisMax !== null ? yAxisMax : dataMax;
                                    const currentMin = yAxisMin !== null ? yAxisMin : dataMin;
                                    
                                    return (
                                        <div className="d-flex align-items-center gap-2">
                                            <label className="text-muted small mb-0 me-2">
                                                <i className="bi bi-arrows-vertical me-1"></i>
                                                Y-Axis Range:
                                            </label>
                                            <div className="btn-group btn-group-sm" role="group">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => {
                                                        const range = currentMax - currentMin;
                                                        const newRange = range * 0.8; // Zoom in (reduce range by 20%)
                                                        const center = (currentMax + currentMin) / 2;
                                                        setYAxisMax(center + newRange / 2);
                                                        setYAxisMin(Math.max(0, center - newRange / 2)); // Don't go below 0
                                                    }}
                                                    title="Zoom in (reduce Y-axis range)"
                                                >
                                                    <i className="bi bi-zoom-in"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => {
                                                        const range = currentMax - currentMin;
                                                        const newRange = range * 1.25; // Zoom out (increase range by 25%)
                                                        const center = (currentMax + currentMin) / 2;
                                                        setYAxisMax(center + newRange / 2);
                                                        setYAxisMin(Math.max(0, center - newRange / 2)); // Don't go below 0
                                                    }}
                                                    title="Zoom out (increase Y-axis range)"
                                                >
                                                    <i className="bi bi-zoom-out"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => {
                                                        setYAxisMax(null);
                                                        setYAxisMin(null);
                                                    }}
                                                    title="Reset to auto-scale"
                                                >
                                                    <i className="bi bi-arrow-counterclockwise"></i> Reset
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                            {loading && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 10,
                                    borderRadius: '0.375rem'
                                }}>
                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading, please wait...</p>
                                </div>
                            )}
                            {chartData.series.length > 0 ? (
                                <div style={{ width: '100%', overflow: 'hidden' }}>
                                    <Chart
                                        options={chartData.options}
                                        series={chartData.series}
                                        type="line"
                                        height={450}
                                    />
                                </div>
                            ) : (
                                !loading && (
                                    <div className="text-center py-5">
                                        <i className="bi bi-graph-up" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                        <p className="text-muted mt-3">
                                            {selectedResource
                                                ? 'No data available for the selected date range and resource'
                                                : 'Please select a resource to view the report'}
                                        </p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PerformanceManagementReport;

